import React, { useState, useEffect } from 'react';
import { User as UserType } from '@supabase/supabase-js';
import { 
  User, 
  Settings, 
  Shield, 
  LogOut, 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertTriangle,
  Gift,
  Users,
  Clock,
  ChevronRight,
  Copy,
  Save
} from 'lucide-react';
import TwoFactorSetup from './TwoFactorSetup';
import KycDocumentUpload from './KycDocumentUpload';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabaseClient';

interface ProfilePageProps {
  user: UserType;
  usdtBalance: number;
  btcBalance: number;
  currentPrice: number;
  onSignOut: () => void;
  onUpdatePassword: (password: string) => Promise<any>;
  referralCode: string | null;
  referralCount: number;
  referredUsers: any[];
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  user,
  usdtBalance,
  btcBalance,
  currentPrice,
  onSignOut,
  onUpdatePassword,
  referralCode,
  referralCount,
  referredUsers
}) => {
  const { twoFactorEnabled, disableTwoFactor } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'referrals'>('profile');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [showKycUpload, setShowKycUpload] = useState(false);
  const [kycStatus, setKycStatus] = useState<'not_verified' | 'pending' | 'verified'>('not_verified');
  
  // Profile information state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const totalPortfolioValue = usdtBalance + (btcBalance * currentPrice);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, country, kyc_status')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }
        
        if (data) {
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
          setCountry(data.country || '');
          setKycStatus(data.kyc_status || 'not_verified');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, [user.id]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const { error } = await onUpdatePassword(newPassword);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!twoFactorCode) {
      setError('Please enter your two-factor authentication code');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await disableTwoFactor(twoFactorCode);
      
      if (result.error) {
        throw result.error;
      }
      
      setSuccess('Two-factor authentication disabled successfully');
      setTwoFactorCode('');
      setDisabling2FA(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setSuccess('Referral code copied to clipboard');
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError(null);
    setSuccess(null);
    
    try {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: firstName,
          last_name: lastName,
          country: country
        })
        .eq('id', user.id);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Profile information saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile information');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleStartVerification = () => {
    setShowKycUpload(true);
  };

  const handleKycStatusChange = (status: 'not_verified' | 'pending' | 'verified') => {
    setKycStatus(status);
  };

  return (
    <div className="container mx-auto p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
            <User size={32} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Account Settings
            </h1>
            <p className="text-slate-400">{user.email}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="bg-slate-800/50 backdrop-blur-sm px-6 py-3 rounded-xl border border-slate-700/50 shadow-lg">
            <div className="text-slate-400 text-sm">Portfolio Value</div>
            <div className="text-white font-mono text-xl">${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          
          <button
            onClick={onSignOut}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700 mb-8">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'profile' 
              ? 'text-blue-400 border-b-2 border-blue-400' 
              : 'text-slate-400 border-transparent hover:text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <User size={18} />
            Profile
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('security')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'security' 
              ? 'text-blue-400 border-b-2 border-blue-400' 
              : 'text-slate-400 border-transparent hover:text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            Security
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('referrals')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'referrals' 
              ? 'text-blue-400 border-b-2 border-blue-400' 
              : 'text-slate-400 border-transparent hover:text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <Gift size={18} />
            Referrals
          </div>
        </button>
      </div>
      
      {/* Status Messages */}
      {error && (
        <div className="bg-red-600/20 border border-red-600 rounded-xl p-4 flex items-center gap-3 mb-6">
          <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-600/20 border border-green-600 rounded-xl p-4 flex items-center gap-3 mb-6">
          <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{success}</span>
        </div>
      )}
      
      {/* Tab Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left Column - Main Content */}
        <div className="md:col-span-2">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                <User size={20} className="text-blue-400" />
                Profile Information
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={user.email || ''}
                      readOnly
                      className="w-full bg-slate-900/50 text-white pl-10 pr-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">First Name</label>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Last Name</label>
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Country</label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all custom-select"
                  >
                    <option value="">Select your country</option>
                    <option value="US">United States</option>
                    <option value="UK">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="JP">Japan</option>
                    <option value="SG">Singapore</option>
                    <option value="CH">Switzerland</option>
                    <option value="NL">Netherlands</option>
                    <option value="ES">Spain</option>
                    <option value="IT">Italy</option>
                    <option value="SE">Sweden</option>
                    <option value="NO">Norway</option>
                    <option value="DK">Denmark</option>
                    <option value="FI">Finland</option>
                    <option value="NZ">New Zealand</option>
                    <option value="BR">Brazil</option>
                    <option value="MX">Mexico</option>
                    <option value="IN">India</option>
                    <option value="CN">China</option>
                    <option value="RU">Russia</option>
                    <option value="ZA">South Africa</option>
                    <option value="AE">United Arab Emirates</option>
                  </select>
                </div>
                
                {/* KYC Section */}
                <div id="kyc-section" className="bg-slate-900/30 rounded-xl p-6 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-white mb-4">Identity Verification (KYC)</h3>
                  <p className="text-slate-400 mb-4">
                    Complete identity verification to unlock full platform features including withdrawals and higher trading limits.
                  </p>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      kycStatus === 'verified' ? 'bg-green-500/20' : 
                      kycStatus === 'pending' ? 'bg-amber-500/20' : 
                      'bg-amber-500/20'
                    }`}>
                      {kycStatus === 'verified' ? (
                        <CheckCircle size={20} className="text-green-400" />
                      ) : kycStatus === 'pending' ? (
                        <Clock size={20} className="text-amber-400" />
                      ) : (
                        <AlertTriangle size={20} className="text-amber-400" />
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {kycStatus === 'verified' ? 'Verification Complete' : 
                         kycStatus === 'pending' ? 'Verification Pending' : 
                         'Verification Required'}
                      </div>
                      <div className="text-sm text-slate-400">
                        {kycStatus === 'verified' ? 'Your account is fully verified' : 
                         kycStatus === 'pending' ? 'Your documents are being reviewed' : 
                         'Your account is not verified'}
                      </div>
                    </div>
                  </div>
                  
                  {kycStatus === 'not_verified' && (
                    <button 
                      onClick={handleStartVerification}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                    >
                      Start Verification Process
                      <ChevronRight size={18} />
                    </button>
                  )}
                  
                  {kycStatus === 'pending' && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-400 text-sm">
                      Your verification is in progress. This typically takes 1-3 business days. You'll be notified once the review is complete.
                    </div>
                  )}
                  
                  {kycStatus === 'verified' && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400 text-sm">
                      Your account is fully verified. You now have access to all platform features including withdrawals and higher trading limits.
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                  >
                    {savingProfile ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={18} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-8">
              {/* Change Password */}
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                  <Lock size={20} className="text-blue-400" />
                  Change Password
                </h2>
                
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Current Password</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        className="w-full bg-slate-900/50 text-white pl-10 pr-10 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">New Password</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="w-full bg-slate-900/50 text-white pl-10 pr-10 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-1">
                      Must be at least 6 characters
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Confirm New Password</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full bg-slate-900/50 text-white pl-10 pr-10 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading || !newPassword || !confirmPassword}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        'Update Password'
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              {/* Two-Factor Authentication */}
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                  <Shield size={20} className="text-blue-400" />
                  Two-Factor Authentication
                </h2>
                
                {showTwoFactorSetup ? (
                  <TwoFactorSetup 
                    onComplete={() => {
                      setShowTwoFactorSetup(false);
                      setSuccess('Two-factor authentication enabled successfully');
                    }}
                    onCancel={() => setShowTwoFactorSetup(false)}
                  />
                ) : disabling2FA ? (
                  <form onSubmit={handleDisableTwoFactor} className="space-y-6">
                    <p className="text-slate-300 mb-4">
                      Enter your two-factor authentication code to disable 2FA.
                    </p>
                    
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">
                        Verification Code
                      </label>
                      <input
                        type="text"
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        placeholder="Enter 6-digit code"
                        className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        maxLength={6}
                        pattern="[0-9]{6}"
                        required
                      />
                    </div>
                    
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={() => setDisabling2FA(false)}
                        className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      
                      <button
                        type="submit"
                        disabled={loading || twoFactorCode.length !== 6}
                        className="bg-red-600 hover:bg-red-700 disabled:bg-slate-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          'Disable 2FA'
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className="text-white font-medium mb-1">
                          {twoFactorEnabled ? 'Enabled' : 'Not Enabled'}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {twoFactorEnabled 
                            ? 'Your account is protected with two-factor authentication.' 
                            : 'Add an extra layer of security to your account.'}
                        </p>
                      </div>
                      
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        twoFactorEnabled 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </div>
                    </div>
                    
                    <p className="text-slate-300 text-sm mb-6">
                      Two-factor authentication adds an extra layer of security to your account by requiring a code from your phone in addition to your password.
                    </p>
                    
                    {twoFactorEnabled ? (
                      <button
                        onClick={() => setDisabling2FA(true)}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                      >
                        Disable Two-Factor Authentication
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowTwoFactorSetup(true)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20"
                      >
                        Enable Two-Factor Authentication
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <div className="space-y-8">
              {/* Referral Program */}
              <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                  <Gift size={20} className="text-blue-400" />
                  Referral Program
                </h2>
                
                <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl p-6 border border-blue-500/20 mb-6">
                  <h3 className="text-lg font-semibold text-white mb-2">Invite Friends & Earn Rewards</h3>
                  <p className="text-slate-300 mb-4">
                    Share your referral code with friends and earn rewards when they sign up and start trading.
                  </p>
                  
                  <div className="mb-4">
                    <label className="block text-sm text-slate-400 mb-2">Your Referral Code</label>
                    <div className="flex">
                      <input
                        type="text"
                        value={referralCode || 'No referral code available'}
                        readOnly
                        className="flex-1 bg-slate-900/50 text-white px-4 py-3 rounded-l-xl border border-slate-600/50 focus:outline-none"
                      />
                      <button
                        onClick={copyReferralCode}
                        disabled={!referralCode}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white px-4 py-3 rounded-r-xl border-y border-r border-blue-600 flex items-center gap-2"
                      >
                        <Copy size={16} />
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                      <div className="text-3xl font-bold text-white mb-1">{referralCount}</div>
                      <div className="text-slate-400 text-sm">Referrals</div>
                    </div>
                    
                    <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                      <div className="text-3xl font-bold text-white mb-1">5%</div>
                      <div className="text-slate-400 text-sm">Commission</div>
                    </div>
                    
                    <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                      <div className="text-3xl font-bold text-white mb-1">$0.00</div>
                      <div className="text-slate-400 text-sm">Earned</div>
                    </div>
                  </div>
                </div>
                
                {/* Referral Link */}
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-2">Your Referral Link</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={referralCode ? `${window.location.origin}/ref/${referralCode}` : 'No referral link available'}
                      readOnly
                      className="flex-1 bg-slate-900/50 text-white px-4 py-3 rounded-l-xl border border-slate-600/50 focus:outline-none"
                    />
                    <button
                      onClick={() => {
                        if (referralCode) {
                          navigator.clipboard.writeText(`${window.location.origin}/ref/${referralCode}`);
                          setSuccess('Referral link copied to clipboard');
                          
                          setTimeout(() => {
                            setSuccess(null);
                          }, 3000);
                        }
                      }}
                      disabled={!referralCode}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white px-4 py-3 rounded-r-xl border-y border-r border-blue-600 flex items-center gap-2"
                    >
                      <Copy size={16} />
                      Copy
                    </button>
                  </div>
                </div>
                
                {/* Referred Users */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Users size={18} className="text-blue-400" />
                    Referred Users
                  </h3>
                  
                  {referredUsers.length > 0 ? (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {referredUsers.map((user, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-900/30 p-3 rounded-lg border border-slate-700/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                              <User size={16} className="text-slate-300" />
                            </div>
                            <div>
                              <div className="text-white font-medium">{user.email}</div>
                              <div className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock size={12} />
                                {new Date(user.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">
                            Active
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-900/30 rounded-xl border border-slate-700/50">
                      <Gift size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="mb-2">No referrals yet</p>
                      <p className="text-sm">Share your referral code to start earning rewards</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column - Account Summary */}
        <div className="space-y-8">
          {/* Account Summary */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Account Summary</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Account Type</span>
                <span className="text-white font-medium">Standard</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Member Since</span>
                <span className="text-white font-medium">{new Date(user.created_at || Date.now()).toLocaleDateString()}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-400">KYC Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  kycStatus === 'verified' ? 'bg-green-500/20 text-green-400' :
                  kycStatus === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>
                  {kycStatus === 'verified' ? 'Verified' :
                   kycStatus === 'pending' ? 'Pending' :
                   'Not Verified'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-400">2FA Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  twoFactorEnabled 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-700/50">
              <button 
                onClick={() => setActiveTab('profile')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
              >
                Complete Your Profile
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          
          {/* Account Balances */}
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Account Balances</h3>
            
            <div className="space-y-4">
              <div className="bg-slate-900/30 rounded-lg p-4 border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                      <span className="text-green-400 font-bold">$</span>
                    </div>
                    <span className="text-white font-medium">USDT</span>
                  </div>
                  <span className="text-white font-mono">{usdtBalance.toFixed(2)}</span>
                </div>
                <div className="text-xs text-slate-400 text-right">
                  ${usdtBalance.toFixed(2)} USD
                </div>
              </div>
              
              <div className="bg-slate-900/30 rounded-lg p-4 border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <span className="text-orange-400 font-bold">₿</span>
                    </div>
                    <span className="text-white font-medium">BTC</span>
                  </div>
                  <span className="text-white font-mono">{btcBalance.toFixed(8)}</span>
                </div>
                <div className="text-xs text-slate-400 text-right">
                  ${(btcBalance * currentPrice).toFixed(2)} USD
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KYC Document Upload Modal */}
      {showKycUpload && (
        <KycDocumentUpload 
          onClose={() => setShowKycUpload(false)}
          onKycStatusChange={handleKycStatusChange}
        />
      )}
    </div>
  );
};

export default ProfilePage;